import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, message, Popconfirm, Table, Tag } from "antd";
import Header from "../../components/Shared/Header";
import {
  deleteLicensePermanently,
  deactivateLicense,
  getAllLicenses,
  reactivateLicense,
} from "../../Utils/licenses/licenses";

const LicensesPage = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoadingByKey, setActionLoadingByKey] = useState({});

  const setRowLoading = (licenseKey, isLoading) => {
    setActionLoadingByKey((prev) => ({
      ...prev,
      [licenseKey]: isLoading,
    }));
  };

  const fetchLicenses = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getAllLicenses();
      setLicenses(response.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to load licenses");
      message.error("Erreur lors du chargement des licences");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  const handleDeactivate = async (record) => {
    setRowLoading(record.license_key, true);

    try {
      await deactivateLicense(record.hardware_fingerprint);
      message.success("Licence desactivee avec succes");
      await fetchLicenses();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Impossible de desactiver la licence";
      message.error(detail);
    } finally {
      setRowLoading(record.license_key, false);
    }
  };

  const handleReactivate = async (record) => {
    setRowLoading(record.license_key, true);

    try {
      await reactivateLicense({
        license_key: record.license_key,
        hardware_fingerprint: record.hardware_fingerprint,
        device_name: record.device_name,
        reason: "Reactivation from admin dashboard",
      });
      message.success("Licence reactivee avec succes");
      await fetchLicenses();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Impossible de reactiver la licence";
      message.error(detail);
    } finally {
      setRowLoading(record.license_key, false);
    }
  };

  const handlePermanentDelete = async (record) => {
    setRowLoading(record.license_key, true);

    try {
      await deleteLicensePermanently(record.hardware_fingerprint);
      message.success("Licence supprimee definitivement avec succes");
      await fetchLicenses();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Impossible de supprimer definitivement la licence";
      message.error(detail);
    } finally {
      setRowLoading(record.license_key, false);
    }
  };

  const columns = [
    {
      title: "Device",
      dataIndex: "device_name",
      key: "device_name",
      render: (value) => value || "-",
      sorter: (a, b) => (a.device_name || "").localeCompare(b.device_name || ""),
    },
    {
      title: "License Key",
      dataIndex: "license_key",
      key: "license_key",
      render: (value) => value || "-",
    },
    {
      title: "Hardware Fingerprint",
      dataIndex: "hardware_fingerprint",
      key: "hardware_fingerprint",
      render: (value) => value || "-",
    },
    {
      title: "Type",
      dataIndex: "license_type",
      key: "license_type",
      render: (value) => value || "-",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "red"}>{isActive ? "Active" : "Inactive"}</Tag>
      ),
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: "Last Validation",
      dataIndex: "last_online_validation",
      key: "last_online_validation",
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
      sorter: (a, b) => {
        const dateA = a.last_online_validation ? new Date(a.last_online_validation).getTime() : 0;
        const dateB = b.last_online_validation ? new Date(b.last_online_validation).getTime() : 0;
        return dateA - dateB;
      },
    },
    {
      title: "Validations",
      dataIndex: "validation_count",
      key: "validation_count",
      render: (value) => value ?? 0,
      sorter: (a, b) => (a.validation_count ?? 0) - (b.validation_count ?? 0),
    },
    {
      title: "Action",
      key: "action",
      width: 260,
      render: (_, record) => {
        const isProcessing = !!actionLoadingByKey[record.license_key];

        if (record.is_active) {
          return (
            <Popconfirm
              title="Desactiver cette licence ?"
              okText="Oui"
              cancelText="Non"
              onConfirm={() => handleDeactivate(record)}
            >
              <Button danger loading={isProcessing}>Deactivate</Button>
            </Popconfirm>
          );
        }

        return (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button
              type="primary"
              loading={isProcessing}
              onClick={() => handleReactivate(record)}
            >
              Activate
            </Button>

            <Popconfirm
              title="Suppression definitive de la licence ?"
              description="Cette action est irreversible et supprimera la licence de maniere permanente."
              okText="Supprimer"
              okType="danger"
              cancelText="Annuler"
              onConfirm={() => handlePermanentDelete(record)}
              disabled={isProcessing}
            >
              <Button danger loading={isProcessing}>Delete</Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#fff" }}>
      <Header title="Licenses" />

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ margin: "16px" }}
          closable
        />
      )}

      <div style={{ padding: "24px" }}>
        <Table
          rowKey={(record) => record.license_key}
          dataSource={licenses}
          columns={columns}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} licenses`,
          }}
        />
      </div>
    </div>
  );
};

export default LicensesPage;
